import { AdvisorBot, FinancialContext } from '../types/chat';

export class MockAdvisorBot implements AdvisorBot {
  private factorKeywords = {
    verascore: ['verascore', 'overall', 'score', 'health', 'eligible', 'qualify', 'eligibility'],
    factor_dti: ['dti', 'debt to income', 'income ratio', 'debt ratio', 'approval chances'],
    factor_cash_on_hand: [
      'cash',
      'reserves',
      'available funds',
      'liquid',
      'emergency',
      'cash reserve',
    ],
    factor_spending: ['spending', 'expenses', 'costs', 'cash flow'],
    factor_savings: ['savings', 'save', 'saved', 'deposits'],
    factor_debt: ['debt', 'loans', 'owe', 'obligations'],
    factor_payment_history: [
      'payment',
      'history',
      'payments',
      'paying',
      'delinquency',
      'trends',
      'concerning',
    ],
  };

  private commonQuestions = {
    loanEligibility: [
      "what's this member's loan eligibility",
      'loan eligibility',
      'qualify for loan',
      'eligible for loan',
    ],
    paymentTrends: ['concerning trends', 'payment history trends', 'payment trends'],
    dtiApproval: [
      'debt-to-income ratio affect approval',
      'dti affect approval',
      'approval chances',
    ],
    cashReserves: ['cash reserve situation', 'emergency funds', 'liquid assets'],
    improvementAreas: ['factors need improvement', 'areas to improve', 'improvement needed'],
    lcuRelationship: [
      'lcu relationship',
      'credit union relationship',
      'member relationship',
      'how long have they been a member',
    ],
    employmentHistory: ['employment history', 'job history', 'work history', 'employers'],
    ncuaAdvice: [
      'ncua advice',
      'ncua guidelines',
      'lending guidelines',
      'regulatory considerations',
    ],
  };

  private getFactorFromMessage(message: string): string | null {
    const lowercaseMsg = message.toLowerCase();

    for (const [factor, keywords] of Object.entries(this.factorKeywords)) {
      if (keywords.some(keyword => lowercaseMsg.includes(keyword))) {
        return factor;
      }
    }
    return null;
  }

  private getCommonQuestionType(message: string): string | null {
    const lowercaseMsg = message.toLowerCase();

    for (const [questionType, patterns] of Object.entries(this.commonQuestions)) {
      if (patterns.some(pattern => lowercaseMsg.includes(pattern))) {
        return questionType;
      }
    }
    return null;
  }

  async sendMessage(message: string, context: FinancialContext): Promise<string> {
    const factor = this.getFactorFromMessage(message);
    const questionType = this.getCommonQuestionType(message);

    // Handle common questions first
    if (questionType) {
      switch (questionType) {
        case 'loanEligibility':
          const score = Math.round(context.verascore);
          return `Based on this member's VeraScore of ${score}, ${
            score >= 75
              ? "they're eligible for our premium loan products with the best rates. Their strong financial health makes them an excellent candidate for approval."
              : score >= 60
                ? 'they qualify for standard loan products. While not at our premium tier, they have good approval chances with competitive rates.'
                : score >= 45
                  ? 'they may qualify for basic loan products, but we should consider additional requirements like a co-signer or higher down payment. Would you like me to analyze specific factors that could improve their eligibility?'
                  : "they currently have limited loan options. I'd recommend our secured products or credit builder programs to improve their eligibility."
          } Would you like me to analyze specific factors that could improve their eligibility?`;

        case 'paymentTrends':
          const paymentScore =
            context.factors.find(f => f.key === 'factor_payment_history')?.score || 0;
          return `Looking at their payment history (score: ${paymentScore}), ${
            paymentScore >= 70
              ? "I don't see any concerning trends. They've maintained consistent on-time payments across accounts, which is excellent."
              : paymentScore >= 50
                ? "there are a few minor issues to note. They've had occasional late payments, but nothing that suggests a pattern of delinquency."
                : "there are definitely some concerning patterns. They've had multiple late payments in the past 12 months, which raises red flags about their reliability."
          } ${
            paymentScore < 60
              ? "I'd recommend setting up automatic payments for any new loans to mitigate this risk."
              : 'This positive payment history supports their loan application.'
          }`;

        case 'dtiApproval':
          const dtiScore = context.factors.find(f => f.key === 'factor_dti')?.score || 0;
          return `Their debt-to-income ratio currently scores at ${dtiScore}/100. ${
            dtiScore >= 70
              ? 'This is well within our preferred range, indicating they have sufficient income to take on additional debt. This positively impacts their approval chances.'
              : dtiScore >= 50
                ? 'This is in an acceptable range, though not ideal. They should be able to qualify for standard products, but might not receive our best rates.'
                : "This is higher than our preferred threshold, which negatively impacts their approval chances. We'd need to carefully evaluate their capacity to take on additional debt."
          } ${
            dtiScore < 50
              ? 'Consider suggesting debt consolidation or paydown strategies before applying for new credit.'
              : 'Their DTI ratio supports moving forward with the loan application process.'
          }`;

        case 'cashReserves':
          const cashScore = context.factors.find(f => f.key === 'factor_cash_on_hand')?.score || 0;
          return `Regarding their cash reserves (score: ${cashScore}), ${
            cashScore >= 70
              ? 'they maintain excellent emergency funds. They have sufficient liquid assets to cover several months of expenses, which is a strong positive indicator.'
              : cashScore >= 50
                ? 'they have adequate reserves, though not exceptional. They could likely handle minor financial emergencies without defaulting on obligations.'
                : 'their situation is concerning. They have limited liquid assets available, which increases their vulnerability to payment shocks.'
          } ${
            cashScore < 50
              ? 'This is a risk factor we should consider when evaluating their application. They may struggle with unexpected expenses.'
              : 'Their cash reserve situation supports their ability to manage loan payments even if they face unexpected expenses.'
          }`;

        case 'improvementAreas':
          // Find the lowest scoring factors
          const sortedFactors = [...context.factors].sort((a, b) => a.score - b.score);
          const lowestFactors = sortedFactors.slice(0, 2);

          const factorNames: Record<string, string> = {
            factor_dti: 'debt-to-income ratio',
            factor_cash_on_hand: 'cash reserves',
            factor_spending: 'spending patterns',
            factor_savings: 'savings rate',
            factor_debt: 'debt management',
            factor_payment_history: 'payment history',
            verascore: 'overall VeraScore',
          };

          return `Based on their profile, the areas needing the most improvement are:
          
1. ${factorNames[lowestFactors[0]?.key || 'factor_dti']} (score: ${lowestFactors[0]?.score || 0})
2. ${factorNames[lowestFactors[1]?.key || 'factor_savings']} (score: ${lowestFactors[1]?.score || 0})

Focusing on these areas would have the greatest impact on improving their overall financial health and loan eligibility. Would you like specific recommendations for improving either of these factors?`;

        case 'lcuRelationship':
          // Mock data for LCU relationship
          const membershipYears = Math.floor(Math.random() * 15) + 1;
          const accountTypes = ['Checking', 'Savings', 'Credit Card', 'Auto Loan'];
          const numAccounts = Math.floor(Math.random() * 3) + 1;
          const memberAccounts = accountTypes.slice(0, numAccounts);

          return `This member has been with our credit union for ${membershipYears} years. They currently have ${numAccounts} active accounts: ${memberAccounts.join(', ')}. ${
            membershipYears > 10
              ? "They're one of our long-term members with an excellent relationship history."
              : membershipYears > 5
                ? "They've been a consistent member with a good relationship history."
                : "They're a relatively new member, but have been actively using our services."
          } Would you like to view their full member profile or discuss specific relationship-building opportunities?`;

        case 'employmentHistory':
          // Mock data for employment history
          const currentEmployer = 'TechCorp Industries';
          const currentTenure = Math.floor(Math.random() * 5) + 1;
          const previousEmployer = 'Global Solutions LLC';
          const previousTenure = Math.floor(Math.random() * 3) + 1;
          const employmentStatus = Math.random() > 0.2 ? 'Full-time' : 'Part-time';
          const annualIncome = Math.floor(Math.random() * 50000) + 50000;

          return `Employment summary for the past 2 years:

• Current: ${currentEmployer} (${currentTenure} ${currentTenure === 1 ? 'year' : 'years'}) - ${employmentStatus}
• Previous: ${previousEmployer} (${previousTenure} ${previousTenure === 1 ? 'year' : 'years'})

Their current annual income is approximately $${annualIncome.toLocaleString()}. ${
            currentTenure > 2
              ? 'They show good employment stability, which is favorable for loan approval.'
              : 'Their employment history shows some recent changes, which we should consider when evaluating loan terms.'
          } Would you like more details about their income verification or employment stability assessment?`;

        case 'ncuaAdvice':
          const veraScore = context.verascore;

          return `Based on NCUA lending guidelines and this member's profile (VeraScore: ${veraScore}), here are key considerations:

1. ${veraScore < 60 ? 'Risk-Based Lending: This member falls into a higher risk category, requiring additional documentation and possibly higher rates per NCUA guidelines.' : 'Standard Lending: This member qualifies for standard lending practices under NCUA guidelines.'}

2. Ability to Repay: ${context.factors.find(f => f.key === 'factor_dti')?.score || 0 > 60 ? 'Their debt-to-income ratio indicates good capacity to repay, meeting NCUA requirements.' : 'We should carefully document their ability to repay as required by NCUA regulations.'}

3. Documentation: Ensure we have current income verification, credit reports, and application disclosures as required by NCUA Regulation 701.

Would you like more specific guidance on NCUA compliance for this particular loan type?`;
      }
    }

    // Handle factor-specific queries
    if (factor) {
      const factorScore = context.factors.find(f => f.key === factor)?.score || 0;
      const factorHealth = context.factors.find(f => f.key === factor)?.health || 'unknown';

      switch (factor) {
        case 'verascore':
          const roundedVeraScore = Math.round(factorScore);
          return `Looking at this member's VeraScore of ${roundedVeraScore} (${factorHealth}). ${
            factorScore >= 70
              ? "They're a strong candidate for our premium products! All standard loan products are available with our best rates, and I'd definitely flag them for relationship-building opportunities. Would you like to know which factors are contributing most to their strong score?"
              : factorScore >= 50
                ? 'They qualify for standard products, though we might want some additional verification for larger loans. Would you like me to break down which areas need the most attention?'
                : "We'll need to be cautious here. I'd recommend secured products or our credit-builder programs. Would you like to see their strongest factor to build on?"
          }`;

        case 'factor_dti':
          return `I've analyzed their DTI - current score is ${factorScore} (${factorHealth}). ${
            factorScore >= 70
              ? 'Great news - their debt-to-income ratio is well managed, showing strong capacity for additional credit. Would you like to see their monthly payment capacity?'
              : 'We should take a closer look here. The DTI suggests limited room for new debt. Would you like to see their current monthly obligations breakdown?'
          } I can also show you how this compares to similar members if that would be helpful.`;

        case 'factor_cash_on_hand':
          return `Let me check their liquidity position - score is ${factorScore} (${factorHealth}). ${
            factorScore >= 60
              ? "They're maintaining healthy reserves for debt servicing, which is great to see. Would you like to know their average monthly buffer?"
              : 'Their limited reserves might affect their ability to handle payment shocks. Should we look at their spending patterns to understand why?'
          } I can also show you their reserve trends over the last 6 months.`;

        case 'factor_spending':
          return `Looking at their spending patterns - score is ${factorScore} (${factorHealth}). ${
            factorScore >= 70
              ? "Their spending is very consistent and well-managed. They're a good candidate for automated payments. Would you like to see their typical monthly discretionary spending?"
              : "I'm seeing some irregular spending patterns that we should review. Would you like me to highlight the specific areas of concern?"
          } I can also analyze their spending trends by category if that would help.`;

        case 'factor_savings':
          return `Analyzing their savings behavior - score is ${factorScore} (${factorHealth}). ${
            factorScore >= 60
              ? "They've shown great savings discipline! This suggests they'd be a good fit for our wealth building products. Would you like to see their average monthly savings rate?"
              : 'Their savings history is limited, which might affect their ability to handle unexpected expenses. Should we look at their income stability?'
          } I can also show you how their savings compare to our member averages.`;

        case 'factor_debt':
          return `Let's look at their debt profile - score is ${factorScore} (${factorHealth}). ${
            factorScore >= 70
              ? "They're managing their debt portfolio very well. Great candidate for consolidation or refinancing opportunities. Would you like to see their current debt breakdown?"
              : 'There are some concerns with their debt utilization. Should we review their specific obligations?'
          } ${
            factorScore < 50
              ? 'We might want to require some debt paydown before new credit. Would you like to see which debts are the most problematic?'
              : 'Would you like to see their debt trend over the last 6 months?'
          }`;

        case 'factor_payment_history':
          return `Checking their payment performance - score is ${factorScore} (${factorHealth}). ${
            factorScore >= 80
              ? "Excellent payment history! They're a strong candidate for our best rates and terms. Would you like to see their longest positive payment streak?"
              : 'There are some payment issues we should discuss. Would you like to see the timing and frequency of late payments?'
          } ${
            factorScore < 60
              ? 'We might want to require automated payments. Should we look at their typical payment patterns?'
              : 'Would you like to see their payment reliability by account type?'
          }`;
      }
    }

    // Default response focused on loan evaluation but more conversational
    return "I'd be happy to help evaluate this member's loan eligibility. We can look at their VeraScore, DTI, cash reserves, spending patterns, savings history, debt profile, or payment history. Which aspect would you like to start with? Or would you like a quick overview of their strongest factors?";
  }

  async getInitialMessage(context: FinancialContext): Promise<string> {
    const { verascore } = context;
    const health = this.getHealthStatus(verascore);

    return `VeraScore: ${Math.round(verascore)} (${health}). How can I help evaluate this member's loan eligibility?`;
  }

  private getHealthStatus(score: number): string {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    if (score >= 20) return 'Needs Improvement';
    return 'At Risk';
  }
}
